import { useI18N } from "common/hooks";
import type { VenueDataSourceInput } from "common/utils";
import { resolveVenueDataSource } from "common/utils";
import { formatDateShort } from "common/utils/general";

// The resolution rules live in common/utils/venueDataSource so the app, the
// widget and the business dashboard share them. Re-exported here because this
// module is where web components already ask about badge state.
export type { VenueDataSourceInput };
export { hasVenueDataSourceBadge, isVenueSociabilityVerified } from "common/utils";

export function VenueDataSourceBadge({
  venue,
  className = "flex items-center gap-1",
  iconClassName = "w-5 h-5 shrink-0",
  labelClassName = "label-m-semibold text-fg-neutral-subtle whitespace-nowrap",
}: {
  venue: VenueDataSourceInput;
  className?: string;
  iconClassName?: string;
  labelClassName?: string;
}) {
  const i18n = useI18N();
  const dataSource = resolveVenueDataSource(venue);
  if (dataSource === "none") return null;

  // Same team/user badge as VenueCard's "Variant indicators" — replaces the
  // old businessVerified text everywhere on the venue profile page.
  const BADGE = {
    team: {
      icon: "/images/sociability_verified.svg",
      label: i18n.get("sociabilityVerified"),
    },
    user: {
      icon: "/images/user_updated.svg",
      label: i18n.get("userUpdated"),
    },
  } as const;

  const lastUpdated = formatDateShort(venue.latestReviewDateUpdated || "");

  return (
    <div className={className}>
      <img
        src={BADGE[dataSource].icon}
        alt=""
        aria-hidden="true"
        className={iconClassName}
      />
      <span className={labelClassName}>
        {BADGE[dataSource].label}
        {lastUpdated ? `: ${lastUpdated}` : ""}
      </span>
    </div>
  );
}
